import { useMemo } from 'react';

import { useRecentlySelected } from '@spotify/confidence-core-react';
import { IdentityFragment } from '@spotify/confidence-graphql';

import { useIdentities } from '../useIdentities';

const STORAGE_KEY = 'recently-selected-identities';
const MAX_RECENT_IDENTITIES = 5;

export interface UseRecentlySelectedIdentitiesResult {
  recentIdentities: IdentityFragment[];
  addRecentIdentity: (name: string) => void;
  clearRecentIdentities: () => void;
  isLoading: boolean;
}

export const useRecentlySelectedIdentities =
  (): UseRecentlySelectedIdentitiesResult => {
    const {
      recentItems: recentIdentityNames,
      addRecentItem: addRecentIdentity,
      clearRecentItems: clearRecentIdentities,
    } = useRecentlySelected<string>({
      key: STORAGE_KEY,
      maxItems: MAX_RECENT_IDENTITIES,
    });

    // Build filter to fetch identities by name
    const filter = useMemo(() => {
      if (recentIdentityNames.length === 0) {
        return undefined;
      }
      return `name:(${recentIdentityNames.map((name: string) => `"${name}"`).join(' OR ')})`;
    }, [recentIdentityNames]);

    const { identities, isLoading } = useIdentities({
      filter,
      pageSize: MAX_RECENT_IDENTITIES,
      skip: !filter,
    });

    // Maintain order based on recentIdentityNames (most recent first)
    // and filter out any identities that no longer exist
    const recentIdentities = useMemo(() => {
      if (!identities) return [];

      const identityMap = new Map(identities.map(i => [i.name, i]));
      return recentIdentityNames
        .map((name: string) => identityMap.get(name))
        .filter(
          (
            identity: IdentityFragment | undefined,
          ): identity is IdentityFragment => identity !== undefined,
        );
    }, [identities, recentIdentityNames]);

    return {
      recentIdentities,
      addRecentIdentity,
      clearRecentIdentities,
      isLoading,
    };
  };
